import { ICityItemToBilling, IProductItemToBilling } from "api/interfaces";
import MeasurementModel from "api/models/MeasurementModel";
import MeasurementService from "api/services/MeasurementService";
import SendToBillingService from "api/services/SendToBillingService";
import { makeAutoObservable } from "mobx";
import {
  decimal,
  dividedBy,
  isGreaterThan,
  percentualToValue,
  subtract,
  sum,
  times,
  valueToPercentual,
} from "utils/DecimalPrecision";
import {
  ContractStore,
  ContractIntegrationStore as Integration,
  MeasurementListStore,
  MeasurementStore,
  MessageStore,
} from "..";
import { NoItemsToBillingException } from "../../api/exceptions/MeasurementException";
import {
  IBillingDataItem,
  IBranch,
  ICity,
  IClient,
  ICostCenter,
  IDocumentType,
  IPaymentCondition,
  IProject,
} from "../../api/interfaces/megaERP";
import { TOption } from "../../components/UI/Fields/AutoCompleteField";
import { lang } from "../../i18nLang";

class Store {
  /** Medição selecionada para envio para faturamento. */
  public selectedMeasurement?: MeasurementModel;
  /** Propriedade que armazena as informações escolhidas pelo usuário do campos da integração. */
  private _integrationData: {
    client?: IClient;
    branch?: IBranch;
    paymentCondition?: IPaymentCondition;
    documentType?: IDocumentType;
    costCenter?: ICostCenter;
    projectErp?: IProject;
  } = {};
  /** Total da medição que será faturada. */
  public totalMeasured: number = 0;
  /** Flag que sinaliza se existe algum erro no formulário dos dados de integração. */
  public integrationDataHasError: boolean = true;
  /** Items de produtos com os campos a mais necessários para o faturamento. */
  public productsItems: IProductItemToBilling[] = [];
  /** Flag com o total de itens selecionados usada para forçar a renderização da tabela de produtos. */
  public selectedProductsItemsCount: number = 0;
  /** Armazena o último index do item de produtos selecionado. */
  private lastProductSelectedIndex: number = 0;
  /** Total de rateio aplicado pelo usuário nos produtos selecionados. Usada para verificar se o
   * usuário já usou todo o valor medido ou não.
   */
  public productsItemsApportioningTotal: number = 0;
  /** Flag que sinaliza que os produtos estão sendo carregados. */
  public loadingProducts: boolean = false;
  /** Termo pesquisado no campo de busca de itens de produtos. */
  public productsSearchTerm: string = "";
  /** Flag que sinaliza se o rateio será feito automático ou manualmente pelo usuário. */
  public manualApportioning: boolean = false;
  /** Items de municípios com os campos a mais necessários para o faturamento. */
  public citiesItems: ICityItemToBilling[] = [];
  /** Total de rateio aplicado pelo usuário nos municípios selecionados. Usada para verificar se o
   * usuário já usou todo o valor medido ou não.
   */
  public citiesItemsApportioningTotal: number = 0;
  /** Armazena o último ID usado nos itens de municípios. */
  public lastCityID: number = 0;
  /** Sinaliza se o valor de rateio dos itens deve ser separado em medição
   * e reajuste.
   */
  public separateReadjust: boolean = false;
  /** Lista final com os itens prontos para serem enviados para faturamento. */
  public itemsToSend: IBillingDataItem[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get integrationData() {
    return { ...this._integrationData };
  }

  reset() {
    this.selectedMeasurement = undefined;
    this._integrationData = {
      client: undefined,
      branch: undefined,
      paymentCondition: undefined,
      documentType: undefined,
      costCenter: undefined,
      projectErp: undefined,
    };
    this.totalMeasured = 0;
    this.integrationDataHasError = true;
    this.productsItems = [];
    this.selectedProductsItemsCount = 0;
    this.lastProductSelectedIndex = 0;
    this.productsItemsApportioningTotal = 0;
    this.loadingProducts = false;
    this.productsSearchTerm = "";
    this.manualApportioning = false;
    this.citiesItems = [];
    this.lastCityID = 0;
    this.separateReadjust = false;
    this.itemsToSend = [];
  }

  /** Valida se os dados de integração informados são válidos. */
  integrationDataAreValid() {
    if (this.integrationDataHasError) return false;
    if (
      !this._integrationData.client ||
      !this._integrationData.branch ||
      !this._integrationData.paymentCondition ||
      !this._integrationData.documentType
    )
      return false;

    return true;
  }

  /** Valida se os dados dos produtos selecionados estão válidos. Ou seja,
   * se o usuário aplicou todo valor total da medição a ser faturada no
   * campo de valor de rateio de cada produto selecionado.
   */
  productsItemsDataAreValid() {
    const selectedItem = this.productsItems.filter((item) => item.selected);
    const hasEmptyItem = selectedItem.some(
      (item) =>
        item.apportionmentValue === undefined || item.apportionmentValue === 0
    );
    if (hasEmptyItem) return false;

    return decimal(this.productsItemsApportioningTotal, 4).isEqualTo(
      this.totalMeasured
    );
  }

  /** Valida se os dados dos municípios estão válidos. Ou seja,
   * se todos os itens foram preenchidos e se o usuário aplicou
   * todo valor total da medição a ser faturada no campo de valor
   * de rateio para cada município.
   */
  citiesItemsDataAreValid() {
    const hasEmptyItem = this.citiesItems.some(
      (item) =>
        item.city === undefined ||
        item.apportionmentValue === undefined ||
        item.apportionmentValue === 0
    );
    if (hasEmptyItem) return false;

    return decimal(this.citiesItemsApportioningTotal, 4).isEqualTo(
      this.totalMeasured
    );
  }

  /** Obtém o saldo restante da medição subtraindo o rateio total
   * dos municípios do total medido. */
  getCitiesBalanceOfTotalMeasured() {
    let balance = decimal(this.totalMeasured)
      .subtract(this.citiesItemsApportioningTotal)
      .toNumber();

    if (decimal(balance, 2).isEqualTo(0)) balance = 0;

    return balance;
  }

  /** Obtém o saldo restante da medição subtraindo o rateio total do total medido. */
  getProductsBalanceOfTotalMeasured() {
    let balance = decimal(this.totalMeasured)
      .subtract(this.productsItemsApportioningTotal)
      .toNumber();

    if (decimal(balance, 2).isEqualTo(0)) balance = 0;

    return balance;
  }

  /** Retorna se é permitido fazer o envio do faturamento para
   * medição atual ou informada.
   */
  canSendMeasurement(): boolean {
    try {
      const measurement = this.selectedMeasurement;
      if (!measurement) return false;

      const canSendToBilling =
        !measurement.hasBeenSentToBilling() ||
        measurement.hasBillingOrderError();
      return measurement.canEdit() && canSendToBilling;
    } catch (error) {}

    return false;
  }

  /** Obtém os dados da medição e do ERP Mega para fazer o envio
   * do faturamento.
   */
  async getDataToSendBilling(measurementId?: string) {
    this.reset();
    try {
      this.getMeasurementData(measurementId);
      await this.getIntegrationData();

      const documentTypeId = this.integrationData.documentType?.id;
      if (documentTypeId) this.getProductsItemsData(documentTypeId);

      const branchAgentId = this.integrationData.branch?.agentId;
      if (branchAgentId) this.getCitiesItemsData(branchAgentId);
    } catch (error: any) {
      console.error(error);
      MessageStore.error(lang("contract.errors.fetchData"));
    }
  }

  /** Obtém os dados de integração do contrato atualmente aberto. */
  private async getIntegrationData() {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    await Integration.getDataToContract();
    Integration.fetchAndSetProductItems(contract.documentType?.id as string);

    let client: IClient | undefined = undefined;
    if (contract.development.customerId) {
      client = {
        name: contract.development.customer,
        id: contract.development.customerId || 0,
      };
    }

    this._integrationData = {
      client,
      branch: Integration.findBranch(contract.branchId || 0),
      paymentCondition: Integration.findPaymentCondition(contract),
      documentType: Integration.findDocumentType(contract),
      costCenter: Integration.findCostCenter(contract),
      projectErp: Integration.findProjectErp(contract),
    };
  }

  /** Obtém os dados da medição que será faturada. */
  private getMeasurementData(measurementId?: string) {
    const currentMeasurement = measurementId
      ? MeasurementListStore.findMeasurement(measurementId)
      : MeasurementStore.currentMeasurement;

    if (!currentMeasurement) return;

    this.selectedMeasurement = currentMeasurement;
    this.totalMeasured = decimal(currentMeasurement.getGrossTotal(), 2).round();
  }

  /** Obtém os produtos disponíveis no ERP de acordo
   * com o tipo de documento informado.
   */
  getProductsItemsData(documentTypeId: string) {
    this.loadingProducts = true;
    return Integration.fetchAndSetProductItems(documentTypeId)
      .then(() => {
        this.productsItems = Integration.productItems.map((item) => ({
          ...item,
          materialUsed: false,
          selected: false,
          selectedIndex: undefined,
          apportionmentValue: undefined,
          apportionmentPercentual: undefined,
          measurementValue: 0,
        }));
      })
      .finally(() => {
        this.loadingProducts = false;
      });
  }

  /** Obtém a cidade da filial informada. */
  async getCitiesItemsData(branchAgentId: string) {
    const branchCityName = await Integration.fetchBranchsCity(branchAgentId);
    const city = Integration.findCityByName(branchCityName);
    if (!city) return;

    this.lastCityID = 0;
    this.citiesItems = [
      {
        id: 0,
        city,
        apportionmentValue: this.totalMeasured,
        apportionmentPercentage: 100,
        measurementValue: 0,
        readjustmentValue: 0,
      },
    ];
    this.calculateCitysMeasurementAndReadjustValues(0);
    this.calculateCitiesItemsApportioningTotal();
  }

  /** Obtém os itens prontos para envio do faturamento
   * com os valores de rateio, descontos e caução calculados.
   */
  getItemsReadyToSend() {
    this.itemsToSend = [];
    const measurement = this.selectedMeasurement;
    if (!measurement) return;

    const selectedProductItems = this.productsItems
      .filter((item) => item.selected)
      .sort((a, b) => (a.selectedIndex || 0) - (b.selectedIndex || 0));

    this.itemsToSend = SendToBillingService.createItemsToSend(
      measurement,
      selectedProductItems,
      this.citiesItems,
      this.separateReadjust
    );
  }

  /** Faz o envio das informações preenchidas pelo usuário
   * para a API de geração de documento de venda do ERP Mega.
   */
  async sendAndSaveDataToBilling() {
    const measurement = this.selectedMeasurement;
    if (!measurement) return;

    if (!Integration.isAuthenticated) return;

    try {
      const dataSent = await SendToBillingService.prepareAndSendToBilling(
        Integration.api,
        measurement,
        this.itemsToSend,
        this._integrationData
      );
      await MeasurementService.updateMeasurementToBilling(
        measurement,
        dataSent.data.branch.id,
        dataSent.orderId,
        dataSent.data.issueDate
      );
      await MeasurementService.updateMeasurementBillingData(
        measurement,
        dataSent.data
      );

      MessageStore.success(lang("billing.success"));
    } catch (error) {
      console.error(error);
      if (error instanceof NoItemsToBillingException)
        MessageStore.warning(error.message);
      else MessageStore.error(lang("billing.error"));
    }
  }

  /** Altera os valores do campo integrationData, os dados
   * da integração a serem enviados. */
  setIntegrationData(name: string, value: TOption) {
    const newData = { ...this._integrationData };
    newData[name] = value;
    this._integrationData = newData;
  }

  /** Altera o valor do campo integrationDataHasError, sinalizado que
   * possui ou não um erro no formulário dos dados de integração.
   */
  setIntegrationHasError(hasError: boolean) {
    this.integrationDataHasError = hasError;
  }

  /** Altera a propriedade productsSearchTerm, o termo pesquisado. */
  setProductsSearchTerm(newTerm: string) {
    newTerm = newTerm || "";
    if (newTerm !== this.productsSearchTerm) this.productsSearchTerm = newTerm;
  }

  /** Altera a propriedade separateReadjust. Ou seja, que
   * o valor de rateio para reajuste deve ser calculado separado
   * do valor da medição.
   */
  setSeparateReadjust(separate: boolean) {
    this.separateReadjust = separate;
  }

  /** Altera a propriedade manualApportioning, que sinaliza se o rateio será
   * automático ou manual; e, se automático, atualiza o rateio de cada produto
   * selecionado e depois soma o valor de rateio total aplicado.
   */
  setManualApportionmentAndUpdateProductsApportionment(manual: boolean) {
    this.manualApportioning = manual;
    this.setAllProductsApportionmentIfAutomatic();
    this.calculateProductsItemsApportionmentTotal();
  }

  /** Altera a propriedade materialUsed do item de produto, sinalizando
   * que aquele item é ou não um material empregado.
   */
  setProductsMaterialUsed(codeItem: number, manual: boolean) {
    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    row.materialUsed = manual;
    return row;
  }

  /** Altera o valor de rateio, apportionmentValue, do item de produto e
   * cálcula o seu percentual atualizando a propriedade apportionmentPercentage.
   */
  setProductsApportionmentValue(codeItem: number, value: number) {
    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    row.apportionmentValue = value;
    row.apportionmentPercentage = this.totalMeasured
      ? valueToPercentual(value, this.totalMeasured)
      : 100;
    return row;
  }

  /** Altera o valor percentual de rateio, apportionmentPercentage, do item de produto
   * e cálcula o seu valor em cima do total medido atualizando a propriedade apportionmentValue.
   */
  setProductsApportionmentPercentage(codeItem: number, percentage: number) {
    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    row.apportionmentValue = percentualToValue(percentage, this.totalMeasured);
    row.apportionmentPercentage = percentage;
    return row;
  }

  /** Função que marca ou desmarca o item de produto como selecionado e depois
   * executa o cálculo do rateio para cada item selecionado, caso o rateio seja manual,
   * no fim atualizando o valor total de rateio aplicado. Caso o item esteja sendo
   * deselecionado, limpa os valores de apportionmentValue e apportionmentPercentage
   * do item.
   */
  selectProdutctItemAndUpdateProductsApportionment(
    codeItem: number,
    selected: boolean
  ) {
    this.setProductsSelected(codeItem, selected);
    if (!selected) this.clearProductItemsApportionmentValues(codeItem);
    this.setAllProductsApportionmentIfAutomatic();
    this.calculateProductsItemsApportionmentTotal();

    const item = this.findProductItemByCodeItem(codeItem);
    return item;
  }

  /** Marca o item como selecionado ou deselecionado e
   * atualiza a contagem de itens selecionados.
   */
  setProductsSelected(codeItem: number, selected: boolean) {
    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    if (selected) {
      this.lastProductSelectedIndex++;
    }

    row.selected = selected;
    row.selectedIndex = selected ? this.lastProductSelectedIndex : undefined;
    this.selectedProductsItemsCount =
      this.selectedProductsItemsCount + (selected ? 1 : -1);
    return row;
  }

  /** Percorre todos os produtos selecionados aplicando o valor de
   * rateio proporcional para cada item.
   */
  setAllProductsApportionmentIfAutomatic() {
    if (this.manualApportioning) return;

    const selectedItems = this.productsItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      return;
    }

    const apportionmentValue = dividedBy(
      this.totalMeasured,
      selectedItems.length
    );
    const apportioningPercentage = valueToPercentual(
      apportionmentValue,
      this.totalMeasured
    );

    selectedItems.forEach((item) => {
      item.apportionmentValue = apportionmentValue;
      item.apportionmentPercentage = apportioningPercentage;
      this.calculateProductsMeasurementValue(item.codItem);
    });
  }

  /** Calcula os valores de medição e reajuste a partir
   * do valor de rateio informado para o item de produto.
   */
  calculateProductsMeasurementValue(
    codeItem: number
  ): IProductItemToBilling | undefined {
    const measurement = this.selectedMeasurement;
    if (!measurement) return;

    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    const apportionmentValue = row.apportionmentValue || 0;
    const readjustValue = percentualToValue(
      measurement.readjustValue,
      row.apportionmentPercentage || 0
    );

    row.measurementValue = subtract(apportionmentValue, readjustValue);
    return row;
  }

  /** Soma o valor de rateio de cada produto e atualiza o
   * total de rateio aplicado.
   */
  calculateProductsItemsApportionmentTotal() {
    const selectedItems = this.productsItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      this.productsItemsApportioningTotal = 0;
      return;
    }

    // Assim dá pra verificar se a divisão ficou correta
    let apportioningTotal = sum(
      ...selectedItems.map((item) => item.apportionmentValue || 0)
    );
    this.productsItemsApportioningTotal = apportioningTotal;
  }

  /** Limpa os valores de apportionmentPercentage e apportionmentValue do item
   * informado.
   */
  clearProductItemsApportionmentValues(codeItem: number) {
    const row = this.findProductItemByCodeItem(codeItem);
    if (!row) return;

    row.apportionmentPercentage = undefined;
    row.apportionmentValue = undefined;
    row.materialUsed = false;
  }

  /** Retorna verdadeiro se o total de rateio dos itens de municípios
   * for maior que o total da medição.
   */
  productsTotalIsGreaterThanTotalMeasured() {
    return isGreaterThan(
      this.productsItemsApportioningTotal,
      this.totalMeasured,
      2
    );
  }

  /** Busca o item pelo seu código de item. */
  private findProductItemByCodeItem(
    codeItem: number
  ): IProductItemToBilling | undefined {
    return this.productsItems.find((item) => item.codItem === codeItem);
  }

  /** Adiciona um item de município a lista de itens. */
  addCitiesItem() {
    const hasBlankCity = Object.values(this.citiesItems).some(
      (item) => item.city === undefined
    );
    if (hasBlankCity) return;

    this.lastCityID++;
    const newCitiesItems = [...this.citiesItems];
    newCitiesItems.push({
      id: this.lastCityID,
      city: undefined,
      apportionmentValue: undefined,
      apportionmentPercentage: undefined,
      measurementValue: 0,
      readjustmentValue: 0,
    });
    this.citiesItems = newCitiesItems;
  }

  /** Remove o item de município da lista pelo
   * index.
   */
  removeCitiesItem(index: number) {
    if (this.citiesItems.length <= 0) return;

    if (this.citiesItems.length === 1) {
      this.citiesItems = [];
      this.addCitiesItem();
      return;
    }

    const newCitiesItems = [...this.citiesItems];
    newCitiesItems.splice(index, 1);
    this.citiesItems = newCitiesItems;
  }

  /** Altera a cidade escolhida no item de município. */
  setCityOfCityItem(index: number, newCity?: ICity) {
    const hasItemWithSameCity = this.citiesItems.some(
      (item) => item.city?.id === newCity?.id
    );
    if (newCity && hasItemWithSameCity) return;

    const row = this.citiesItems[index];
    if (!row) return;

    row.city = newCity;
    return row;
  }

  /** Altera o valor de rateio para o item de município. */
  setCitysApportionmentValue(index: number, value: number) {
    const row = this.citiesItems[index];
    if (!row) return;

    row.apportionmentValue = value;
    row.apportionmentPercentage = this.totalMeasured
      ? valueToPercentual(value, this.totalMeasured)
      : 100;
    return row;
  }

  /** Altera o percentual de rateio para o item de município. */
  setCitysApportionmentPercentage(index: number, percentage: number) {
    const row = this.citiesItems[index];
    if (!row) return;
    row.apportionmentValue = percentualToValue(percentage, this.totalMeasured);
    row.apportionmentPercentage = percentage;
    return row;
  }

  /** Calcula os valores de medição e reajuste a partir
   * do valor de rateio informado para o item de município.
   */
  calculateCitysMeasurementAndReadjustValues(index: number) {
    const measurement = this.selectedMeasurement;
    if (!measurement) return;

    const row = this.citiesItems[index];
    if (!row) return;

    const apportionmentValue = row.apportionmentValue || 0;
    const readjustValue = percentualToValue(
      measurement.readjustValue,
      row.apportionmentPercentage || 0
    );

    row.measurementValue = subtract(apportionmentValue, readjustValue);
    row.readjustmentValue = times(
      row.measurementValue,
      measurement.getReadjustedPercentage()
    );
    return row;
  }

  /** Soma os valores de rateio dos itens de municípios
   * para obter o total de rateio aplicado, citiesItemsApportioningTotal.
   */
  calculateCitiesItemsApportioningTotal() {
    const items = this.citiesItems;
    if (items.length === 0) {
      this.citiesItemsApportioningTotal = 0;
      return;
    }

    // Assim dá pra verificar se a divisão ficou correta
    let apportioningTotal = sum(
      ...items.map((item) => item.apportionmentValue || 0)
    );
    this.citiesItemsApportioningTotal = apportioningTotal;
  }

  /** Retorna verdadeiro se o total de rateio dos itens de municípios
   * for maior que o total da medição.
   */
  citiesTotalIsGreaterThanTotalMeasured() {
    return isGreaterThan(
      this.citiesItemsApportioningTotal,
      this.totalMeasured,
      2
    );
  }
}

const SendToBillingStore = new Store();
export default SendToBillingStore;
